// Cyrqle parse-pdf API client.
// Calls the external API directly from the browser (no backend).
// Falls back to a clear error so the UI can offer demo mode.

import { SENTINEL_API_BASE } from "@/lib/sentinelApiBase";

const BASE = SENTINEL_API_BASE;

const TIMEOUT_MS = 60_000;

export class ParseApiError extends Error {
  constructor(message, { code, detail } = {}) {
    super(message);
    this.name = "ParseApiError";
    this.code = code || "unknown";
    this.detail = detail || null;
  }
}

export async function parsePdfFile(file) {
  if (!file) throw new ParseApiError("No file", { code: "no_file" });

  const isPdf =
    file.type === "application/pdf" ||
    (file.name && file.name.toLowerCase().endsWith(".pdf"));
  if (!isPdf) throw new ParseApiError("Invalid type", { code: "invalid_type" });

  if (file.size > 10 * 1024 * 1024)
    throw new ParseApiError("File too large", { code: "too_large" });

  const formData = new FormData();
  formData.append("file", file, file.name || "report.pdf");

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${BASE}/v1/analyses/parse-pdf`, {
      method: "POST",
      body: formData,
      signal: controller.signal,
      headers: {
        "X-Upload-Source": "landing",
      },
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === "AbortError")
      throw new ParseApiError("Timeout", { code: "timeout" });
    // Most likely CORS or network
    throw new ParseApiError("Network or CORS error", {
      code: "network",
      detail: err.message,
    });
  }
  clearTimeout(timeoutId);

  let data;
  try {
    data = await response.json();
  } catch (e) {
    throw new ParseApiError("Invalid response", { code: "invalid_response" });
  }

  if (!response.ok || !data.success) {
    const msg = (data && (data.message || data.error)) || "Parse failed";
    const lower = String(msg).toLowerCase();
    if (lower.includes("laboratory") || lower.includes("format"))
      throw new ParseApiError(msg, { code: "unsupported_lab", detail: data });
    if (lower.includes("corrupt") || lower.includes("encrypted"))
      throw new ParseApiError(msg, { code: "corrupt", detail: data });
    if (lower.includes("large"))
      throw new ParseApiError(msg, { code: "too_large", detail: data });
    throw new ParseApiError(msg, { code: "parse_failed", detail: data });
  }

  return data;
}
